/**
 * The app's own select.
 *
 * A native `<select>` renders the operating system's dropdown — a grey Chrome
 * list in the middle of a warm, dark, deliberately-themed app. It was the one
 * control that looked like it belonged to somebody else.
 *
 * Replacing a native control means owning everything it gave us for free, so
 * this implements the ARIA listbox pattern properly rather than approximately:
 * open with Enter/Space/arrows, move with arrows and Home/End, jump with
 * type-ahead (a thirteen-language list is unusable without it), close with
 * Escape or a click outside, and return focus to the button when it closes.
 * A GM tabbing through setup at 1am should never be able to tell which
 * controls are ours.
 *
 * Options carry an optional `hint` — the thing a native select cannot do, and
 * the reason this is an upgrade rather than a costume: the campaign picker can
 * show a line of premise under each name, and the audio picker can say what a
 * device actually is.
 */
import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';

/** How long a type-ahead search stays open for another keystroke. */
const TYPEAHEAD_RESET_MS = 700;

export interface SelectOption {
  value: string;
  label: string;
  /** A second line, shown under the label in the list. */
  hint?: string;
}

interface Props {
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  /** Rendered as the field's label. Omit only when a label already exists. */
  label?: ReactNode;
  hint?: string;
  placeholder?: string;
  disabled?: boolean;
  id?: string;
  /** Points at an existing label element when `label` is not used. */
  labelledBy?: string;
}

export function Select({
  value,
  onChange,
  options,
  label,
  hint,
  placeholder = 'Select…',
  disabled = false,
  id,
  labelledBy,
}: Props) {
  const generated = useId();
  const baseId = id ?? generated;
  const labelId = `${baseId}-label`;
  const listId = `${baseId}-list`;

  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [dropUp, setDropUp] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const typed = useRef<{ buffer: string; timer?: number }>({ buffer: '' });

  const selectedIndex = options.findIndex((o) => o.value === value);
  const selected = selectedIndex >= 0 ? options[selectedIndex] : undefined;

  const close = useCallback((focusButton = true) => {
    setOpen(false);
    if (focusButton) buttonRef.current?.focus();
  }, []);

  const openList = useCallback(() => {
    if (disabled || options.length === 0) return;
    // Flip above the button when the list would run off the bottom. Measured
    // on open rather than assumed: this control sits near the foot of a long
    // setup form as often as it sits at the top of a card.
    const rect = buttonRef.current?.getBoundingClientRect();
    if (rect) {
      const room = window.innerHeight - rect.bottom;
      setDropUp(room < 260 && rect.top > room);
    }
    setActive(selectedIndex >= 0 ? selectedIndex : 0);
    setOpen(true);
  }, [disabled, options.length, selectedIndex]);

  // Keep the active option in view while arrowing through a long list.
  useLayoutEffect(() => {
    if (!open) return;
    const node = listRef.current?.querySelector<HTMLElement>(
      `[data-index="${active}"]`,
    );
    node?.scrollIntoView({ block: 'nearest' });
  }, [open, active]);

  useEffect(() => () => window.clearTimeout(typed.current.timer), []);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: PointerEvent) {
      const target = event.target as Node;
      if (
        !listRef.current?.contains(target) &&
        !buttonRef.current?.contains(target)
      ) {
        // No focus restore: the click has already moved focus somewhere the
        // user chose, and yanking it back is worse than leaving it.
        close(false);
      }
    }
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open, close]);

  function commit(index: number) {
    const option = options[index];
    if (!option) return;
    onChange(option.value);
    close();
  }

  function typeAhead(key: string) {
    // The buffer is cleared by a timer, not by comparing timestamps.
    //
    // It used to read `Date.now() - lastKeyAt <= 700`, which is at the mercy
    // of clock granularity: browsers coarsen `Date.now()` to resist
    // fingerprinting, and where the step is a full second — this app's own
    // preview does it, and hardened browser settings do it in the wild —
    // every keystroke looks like a fresh search. The visible symptom is
    // subtle and awful: typing "po" moves to Portuguese on the "p" and then
    // treats "o" as a new search, landing on the next option starting with
    // O. A timer measures elapsed time on the event loop, where nothing is
    // rounding it.
    const extending = typed.current.buffer.length > 0;
    typed.current.buffer += key;
    window.clearTimeout(typed.current.timer);
    typed.current.timer = window.setTimeout(() => {
      typed.current.buffer = '';
    }, TYPEAHEAD_RESET_MS);

    const needle = typed.current.buffer.toLowerCase();
    const from = open ? active : Math.max(selectedIndex, 0);
    // Extending the search includes the option already highlighted; starting
    // a fresh one skips past it. Without that split, "po" would move to
    // Portuguese on the "p" and then search *after* Portuguese for "po",
    // landing on Polish — the word the second keystroke was meant to rule
    // out. Repeated single letters still cycle, which is what starting from
    // the next option gives you.
    const start = extending ? from : from + 1;
    const order = [...options.slice(start), ...options.slice(0, start)];
    const hit = order.find((o) => o.label.toLowerCase().startsWith(needle));
    if (!hit) return;
    const index = options.indexOf(hit);
    if (open) setActive(index);
    else onChange(hit.value);
  }

  function onKeyDown(event: React.KeyboardEvent) {
    if (disabled) return;
    const { key } = event;

    if (!open) {
      if (key === 'Enter' || key === ' ' || key === 'ArrowDown' || key === 'ArrowUp') {
        event.preventDefault();
        openList();
        return;
      }
      if (key.length === 1 && !event.metaKey && !event.ctrlKey && !event.altKey) {
        event.preventDefault();
        typeAhead(key);
      }
      return;
    }

    switch (key) {
      case 'Escape':
        event.preventDefault();
        close();
        break;
      case 'Tab':
        // Tab is a move, not a cancel: let focus leave, just close behind it.
        close(false);
        break;
      case 'ArrowDown':
        event.preventDefault();
        setActive((i) => Math.min(options.length - 1, i + 1));
        break;
      case 'ArrowUp':
        event.preventDefault();
        setActive((i) => Math.max(0, i - 1));
        break;
      case 'Home':
        event.preventDefault();
        setActive(0);
        break;
      case 'End':
        event.preventDefault();
        setActive(options.length - 1);
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        commit(active);
        break;
      default:
        if (key.length === 1 && !event.metaKey && !event.ctrlKey && !event.altKey) {
          event.preventDefault();
          typeAhead(key);
        }
    }
  }

  return (
    <div className="field select">
      {label ? (
        <span className="field__label" id={labelId}>
          {label}
        </span>
      ) : null}
      <div className="select__anchor">
        <button
          ref={buttonRef}
          type="button"
          id={baseId}
          className={open ? 'select__button select__button--open' : 'select__button'}
          disabled={disabled}
          onClick={() => (open ? close() : openList())}
          onKeyDown={onKeyDown}
          role="combobox"
          aria-controls={listId}
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-labelledby={labelledBy ?? (label ? labelId : undefined)}
          aria-activedescendant={open ? `${baseId}-opt-${active}` : undefined}
        >
          <span className={selected ? 'select__value' : 'select__value select__value--empty'}>
            {selected?.label ?? placeholder}
          </span>
          <Chevron />
        </button>

        {open ? (
          <ul
            ref={listRef}
            id={listId}
            role="listbox"
            className={dropUp ? 'select__list select__list--up' : 'select__list'}
            tabIndex={-1}
            aria-labelledby={labelledBy ?? (label ? labelId : undefined)}
          >
            {options.map((option, index) => (
              <li
                key={option.value}
                id={`${baseId}-opt-${index}`}
                data-index={index}
                role="option"
                aria-selected={option.value === value}
                className={[
                  'select__option',
                  index === active ? 'select__option--active' : '',
                  option.value === value ? 'select__option--selected' : '',
                ].join(' ')}
                // pointerdown, not click: the outside-click listener also runs
                // on pointerdown, and a click handler would fire after the list
                // had already closed underneath the cursor.
                onPointerDown={(e) => {
                  e.preventDefault();
                  commit(index);
                }}
                onPointerEnter={() => setActive(index)}
              >
                <span className="select__option-label">{option.label}</span>
                {option.hint ? (
                  <span className="select__option-hint">{option.hint}</span>
                ) : null}
              </li>
            ))}
          </ul>
        ) : null}
      </div>
      {hint ? <span className="field__hint">{hint}</span> : null}
    </div>
  );
}

function Chevron() {
  return (
    <svg
      className="select__chevron"
      viewBox="0 0 12 12"
      width="12"
      height="12"
      aria-hidden="true"
    >
      <path
        d="M2.5 4.5 6 8l3.5-3.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
